import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { deliveryAPI } from '../api/api';
import MapPreview from '../components/MapPreview';

export default function CreateDelivery() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    pickupAddress: '',
    dropoffAddress: '',
    recipientName: '',
    recipientPhone: '',
    dropoffNotes: '',
    pickupNotes: '',
    orderNumber: '',
    items: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  // Simple coord preview state (user can optionally enter coords)
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        pickupAddress: form.pickupAddress,
        dropoffAddress: form.dropoffAddress,
        recipientName: form.recipientName,
        recipientPhone: form.recipientPhone,
        dropoffNotes: form.dropoffNotes,
        pickupNotes: form.pickupNotes,
        orderNumber: form.orderNumber,
        items: form.items,
      };
      if (pickupCoords) payload.pickupCoordinates = pickupCoords;
      if (dropoffCoords) payload.dropoffCoordinates = dropoffCoords;

      const { data } = await deliveryAPI.create(payload);
      setSuccess(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create delivery');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">
            {success.status === 'assigned' ? '🚗' : '📦'}
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            {success.status === 'assigned' ? 'Driver assigned!' : 'Delivery created!'}
          </h2>
          <p className="text-gray-400 mb-2">
            {success.status === 'assigned'
              ? 'A nearby driver has been notified and will accept shortly.'
              : 'Your delivery is queued. A driver will be assigned soon.'}
          </p>
          <p className="text-yellow-400 font-semibold mb-6">Delivery fee: ${success.fee}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setSuccess(null); setForm({ pickupAddress: '', dropoffAddress: '', recipientName: '', recipientPhone: '', dropoffNotes: '', pickupNotes: '', orderNumber: '', items: '' }); }}
              className="px-6 py-2.5 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition"
            >
              New Delivery
            </button>
            <Link
              to="/dashboard"
              className="px-6 py-2.5 border border-gray-700 text-white rounded-xl hover:border-gray-500 transition"
            >
              Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center gap-4">
        <Link to="/dashboard" className="text-gray-400 hover:text-white transition">← Back</Link>
        <h1 className="text-xl font-bold">Create Delivery</h1>
      </header>

      <main className="px-4 md:px-8 py-8 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form */}
          <div>
            <h2 className="text-2xl font-bold mb-6">Delivery Details</h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Pickup */}
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
                <h3 className="font-semibold text-yellow-400 mb-4 flex items-center gap-2">
                  <span>📍</span> Pickup Location
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Pickup Address *</label>
                    <input
                      type="text"
                      value={form.pickupAddress}
                      onChange={(e) => setField('pickupAddress', e.target.value)}
                      required
                      className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                      placeholder="123 Main St, Tulsa, OK 74103"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Pickup Notes</label>
                    <input
                      type="text"
                      value={form.pickupNotes}
                      onChange={(e) => setField('pickupNotes', e.target.value)}
                      className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                      placeholder="Ring doorbell, ask for Maya..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Order Number</label>
                    <input
                      type="text"
                      value={form.orderNumber}
                      onChange={(e) => setField('orderNumber', e.target.value)}
                      className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                      placeholder="#1042"
                    />
                  </div>
                </div>
              </div>

              {/* Dropoff */}
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
                <h3 className="font-semibold text-green-400 mb-4 flex items-center gap-2">
                  <span>🎯</span> Dropoff Location
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Dropoff Address *</label>
                    <input
                      type="text"
                      value={form.dropoffAddress}
                      onChange={(e) => setField('dropoffAddress', e.target.value)}
                      required
                      className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                      placeholder="456 Elm Ave, Tulsa, OK 74104"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5">Recipient Name</label>
                      <input
                        type="text"
                        value={form.recipientName}
                        onChange={(e) => setField('recipientName', e.target.value)}
                        className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5">Recipient Phone</label>
                      <input
                        type="tel"
                        value={form.recipientPhone}
                        onChange={(e) => setField('recipientPhone', e.target.value)}
                        className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                        placeholder="+1 555 000 0000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Delivery Notes</label>
                    <input
                      type="text"
                      value={form.dropoffNotes}
                      onChange={(e) => setField('dropoffNotes', e.target.value)}
                      className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                      placeholder="Leave at door, apartment 2B..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5">Items Description</label>
                    <input
                      type="text"
                      value={form.items}
                      onChange={(e) => setField('items', e.target.value)}
                      className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition"
                      placeholder="2x jerk chicken plates, 1x lemonade"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-lg rounded-2xl transition-all active:scale-95 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Delivery — $8'}
              </button>

              <p className="text-xs text-center text-gray-500">
                Driver guaranteed $5 minimum • You pay $8 on Starter plan
              </p>
            </form>
          </div>

          {/* Map preview */}
          <div className="lg:sticky lg:top-8 self-start">
            <h3 className="font-semibold text-gray-300 mb-3">Route Preview</h3>
            <MapPreview
              pickupCoords={pickupCoords}
              dropoffCoords={dropoffCoords}
              height="400px"
              className="w-full"
            />
            <p className="text-xs text-gray-600 mt-2 text-center">
              Pickup and dropoff shown on the map above
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
